'use client';

import { useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useWorkspace } from '@/contexts/WorkspaceProvider';

/**
 * Shown when the workspace requests fail, in place of the redirect to
 * onboarding NoWorkspaceGuard used to make. The last workspace stays active
 * (React Query keeps its data through a failed refetch); this only says the
 * refresh failed and offers to try again.
 */
const WorkspaceLoadErrorBanner = () => {
  const context = useWorkspace();
  const [isRetrying, setIsRetrying] = useState(false);
  if (!context || context.isLoading || !context.isError) return null;

  const retry = async () => {
    setIsRetrying(true);
    try {
      await Promise.all([context.refetchActiveWorkspace(), context.refetchWorkspaces()]);
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <div
      role="alert"
      // Pinned at the bottom: at the top of <main> it sat under the header's
      // absolutely positioned bell and avatar, which covered the Retry button.
      className="fixed bottom-4 left-1/2 z-50 flex w-[min(640px,calc(100vw-2rem))] -translate-x-1/2 items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800 shadow-lg"
    >
      <span className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
        {context.activeWorkspace
          ? `We couldn't refresh ${context.activeWorkspace.workspace_name || 'your workspace'}. You're still working in it.`
          : "We couldn't load your workspace. This is usually temporary."}
      </span>
      <Button size="sm" variant="outline" onClick={retry} disabled={isRetrying} className="shrink-0 border-amber-300 bg-white text-amber-800 hover:bg-amber-100">
        <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${isRetrying ? 'animate-spin' : ''}`} aria-hidden />
        Retry
      </Button>
    </div>
  );
};

export default WorkspaceLoadErrorBanner;
